import { useState, useCallback } from 'react';
import { SCROLL_EFFECTS_CONFIG, type ScrollPosition, validateScrollThreshold } from '../config/scrollEffects';

/**
 * Custom hook for managing scroll-based fade effects
 * 
 * Provides scroll position tracking and fade effect state management
 * with configurable thresholds and error handling.
 * 
 * @param config - Partial configuration to override defaults
 * @returns Object with scroll state and handler function
 */
export const useScrollFadeEffect = (config?: Partial<typeof SCROLL_EFFECTS_CONFIG>) => {
  // Merge provided config with defaults
  const effectConfig = { ...SCROLL_EFFECTS_CONFIG, ...config };
  
  // Scroll position state with proper typing
  const [scrollPosition, setScrollPosition] = useState<ScrollPosition>({ x: 0, y: 0 });
  const [isScrolling, setIsScrolling] = useState(false);
  
  /**
   * Handle scroll position changes with error handling and validation
   * Updates both position and scrolling state based on threshold
   */
  const handleScrollPositionChange = useCallback(({ x, y }: ScrollPosition) => {
    try {
      setScrollPosition({ x, y });
      
      // Validate and apply scroll threshold
      const validatedThreshold = validateScrollThreshold(effectConfig.ACTIVATION_THRESHOLD);
      setIsScrolling(y > validatedThreshold);
    } catch (error) {
      console.warn('Scroll position update failed:', error);
      // Graceful degradation - continue without effects but maintain basic position tracking
      setScrollPosition({ x, y });
    }
  }, [effectConfig.ACTIVATION_THRESHOLD]);
  
  /**
   * Reset scroll state to initial values
   * Useful for component cleanup or manual reset
   */
  const resetScrollState = useCallback(() => {
    setScrollPosition({ x: 0, y: 0 });
    setIsScrolling(false);
  }, []);
  
  return {
    scrollPosition,
    isScrolling,
    handleScrollPositionChange,
    resetScrollState,
    config: effectConfig,
  };
};

export type UseScrollFadeEffectReturn = ReturnType<typeof useScrollFadeEffect>;